import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useRef, forwardRef } from "react";
import { Input, Modal, CloseButton, extractSystemStyles, Popover, createStyles, } from "@mantine/core";
import { useMergedRef, useId } from "@mantine/hooks";
import { ChevronDown } from "../assets/icons.generated";
const useStyles = createStyles((theme) => ({
    wrapper: {
        ...theme.fn.fontStyles(),
        position: "relative",
        cursor: "pointer",
    },
    input: {
        cursor: "pointer",
        whiteSpace: "nowrap",
        "&:not([data-free-input])::selection": {
            backgroundColor: "transparent",
        },
        "&[data-free-input]": {
            cursor: "text",
        },
        "::placeholder": {
            color: theme.colors.primaryText[0],
        },
    },
}));
const RIGHT_SECTION_WIDTH = {
    xs: 24,
    sm: 30,
    md: 34,
    lg: 40,
    xl: 44,
};
const defaultTransition = {
    in: { opacity: 1, transform: "translateY(0) scale(1)" },
    out: { opacity: 0, transform: "translateY(-25px) scale(0.93)" },
    common: { transformOrigin: "top left" },
    transitionProperty: "opacity, transform",
};
const DateTimePickerBase = forwardRef(({ classNames, className, style, styles, wrapperProps, required, allowFreeInput = false, label, error, id, description, placeholder, shadow, transition = defaultTransition, transitionDuration = 100, size = "sm", children, inputLabel, __staticSelector = "DateTimePickerBase", dropdownOpened, setDropdownOpened, dropdownType = "popover", dropdownPosition = "flip", clearable = true, clearButtonLabel, onClear, positionDependencies = [], zIndex, withinPortal = false, onBlur, onFocus, onChange, onKeyDown, name = "date", sx, amountOfMonths = 1, onDropdownClose, onDropdownOpen, clickOutsideEvents = ["mousedown", "touchstart"], modalZIndex, errorProps, labelProps, descriptionProps, clearButtonTabIndex = 0, unstyled, inputContainer, inputWrapperOrder, modalProps, withAsterisk, ...others }, ref) => {
    const { classes, cx, theme } = useStyles(undefined, {
        classNames,
        styles,
        unstyled,
        name: __staticSelector,
    });
    const { systemStyles, rest } = extractSystemStyles(others);
    const uuid = useId(id);
    const inputRef = useRef(null);
    const closeDropdown = () => {
        setDropdownOpened(false);
        onDropdownClose === null || onDropdownClose === void 0 ? void 0 : onDropdownClose();
    };
    const openDropdown = () => {
        setDropdownOpened(true);
        onDropdownOpen === null || onDropdownOpen === void 0 ? void 0 : onDropdownOpen();
    };
    const toggleDropdown = () => {
        setDropdownOpened(!dropdownOpened);
        !dropdownOpened ? onDropdownOpen === null || onDropdownOpen === void 0 ? void 0 : onDropdownOpen() : onDropdownClose === null || onDropdownClose === void 0 ? void 0 : onDropdownClose();
    };
    const closeOnEscape = (event) => {
        if (event.key === "Escape") {
            closeDropdown();
            window.setTimeout(() => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
        }
    };
    const rightSection = clearable ? (_jsx(CloseButton
    // @ts-ignore
    , { 
        // @ts-ignore
        variant: "transparent", sx: { pointerEvents: "all" }, "aria-label": clearButtonLabel, onClick: onClear, size: 16, tabIndex: clearButtonTabIndex, unstyled: unstyled })) : (_jsx(ChevronDown, {}));
    const handleInputBlur = (event) => {
        typeof onBlur === "function" && onBlur(event);
        if (allowFreeInput) {
            closeDropdown();
        }
    };
    const handleInputFocus = (event) => {
        typeof onFocus === "function" && onFocus(event);
        if (allowFreeInput) {
            openDropdown();
        }
    };
    const handleKeyDown = (event) => {
        typeof onKeyDown === "function" && onKeyDown(event);
        if ((event.key === "Space" || event.key === "Enter") && !allowFreeInput) {
            event.preventDefault();
            openDropdown();
        }
    };
    return (_jsx(Input.Wrapper, { required: required, id: uuid, label: label, error: error, description: description, className: className, style: style, classNames: classNames, styles: styles, size: size, __staticSelector: __staticSelector, sx: sx, errorProps: errorProps, descriptionProps: descriptionProps, labelProps: labelProps, inputContainer: inputContainer, inputWrapperOrder: inputWrapperOrder, unstyled: unstyled, withAsterisk: withAsterisk, ...systemStyles, ...wrapperProps, children: _jsxs(Popover, { __staticSelector: __staticSelector, withinPortal: withinPortal, offset: 10, opened: dropdownOpened, transitionDuration: transitionDuration, transition: transition, positionDependencies: positionDependencies, middlewares: { flip: dropdownPosition === "flip", shift: false }, position: dropdownPosition === "flip" ? "bottom-start" : dropdownPosition, shadow: shadow, onClose: closeDropdown, trapFocus: !allowFreeInput, withRoles: false, clickOutsideEvents: clickOutsideEvents, zIndex: zIndex, classNames: classNames, styles: styles, unstyled: unstyled, children: [_jsx(Popover.Target, { children: _jsx("div", { className: classes.wrapper, children: _jsx(Input, { classNames: {
                                ...classNames,
                                input: cx(classes.input, classNames === null || classNames === void 0 ? void 0 : classNames.input),
                            }, "data-free-input": allowFreeInput || undefined, styles: styles, onClick: () => !allowFreeInput ? toggleDropdown() : openDropdown(), onKeyDown: handleKeyDown, id: uuid, ref: useMergedRef(ref, inputRef), __staticSelector: __staticSelector, size: size, name: name, placeholder: placeholder, value: inputLabel, required: required, invalid: !!error, readOnly: !allowFreeInput, rightSection: rightSection, rightSectionWidth: theme.fn.size({
                                size,
                                sizes: RIGHT_SECTION_WIDTH,
                            }), onBlur: handleInputBlur, onFocus: handleInputFocus, onChange: onChange, autoComplete: "off", unstyled: unstyled, ...rest }) }) }), dropdownType === "popover" ? (_jsx(Popover.Dropdown, { children: _jsx("div", { "data-mantine-stop-propagation": dropdownOpened, onKeyDownCapture: closeOnEscape, "aria-hidden": allowFreeInput || undefined, children: children }) })) : (_jsx(Modal, { ...modalProps, opened: dropdownOpened, onClose: closeDropdown, withCloseButton: false, size: amountOfMonths * 400, zIndex: modalZIndex, unstyled: unstyled, children: children }))] }) }));
});
export default DateTimePickerBase;
